import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { adminTeacherAccess, createPair, createUser, logout, users, courses, setTeacherCourseAccess, type User, type Course, type AdminTeacherAccess } from '../lib/api';

interface Props { onLogout: () => void; onOpenMaterials: () => void; }

export default function AdminDashboard({ onLogout, onOpenMaterials }: Props) {
  const [list, setList] = useState<User[]>([]);
  const [courseList, setCourseList] = useState<Course[]>([]);
  const [message, setMessage] = useState('');
  const [userLoadError, setUserLoadError] = useState('');
  const [courseLoadError, setCourseLoadError] = useState('');
  const [teacherAccess, setTeacherAccess] = useState<AdminTeacherAccess | null>(null);
  const [accessBusy, setAccessBusy] = useState('');
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [user, setUser] = useState({ id: '', role: 'teacher' as 'teacher' | 'student', name: '', email: '', password: '' });
  const [pair, setPair] = useState({ id: '', teacher_id: '', student_one_id: '', student_two_id: '', name: '', course_id: '' });

  async function refresh() {
    setLoading(true);
    setUserLoadError('');
    setCourseLoadError('');
    const [userResult, courseResult, accessResult] = await Promise.allSettled([users(), courses(), adminTeacherAccess()]);
    if (userResult.status === 'fulfilled') setList(userResult.value);
    else setUserLoadError(userResult.reason instanceof Error ? userResult.reason.message : 'Could not load users.');
    if (courseResult.status === 'fulfilled') setCourseList(courseResult.value);
    else setCourseLoadError(courseResult.reason instanceof Error ? courseResult.reason.message : 'Could not load courses.');
    if (accessResult.status === 'fulfilled') setTeacherAccess(accessResult.value);
    else setUserLoadError(accessResult.reason instanceof Error ? accessResult.reason.message : 'Could not load teacher access.');
    setLoading(false);
  }
  useEffect(() => { void refresh(); }, []);

  async function addUser(e: FormEvent) {
    e.preventDefault(); setBusy(true); setMessage('');
    try {
      await createUser(user); setMessage('User created successfully.'); setUser({ id:'', role:'teacher', name:'', email:'', password:'' }); await refresh();
    } catch (e) { setMessage(e instanceof Error ? e.message : 'Could not create user.'); } finally { setBusy(false); }
  }

  async function addPair(e: React.FormEvent) {
    e.preventDefault(); setBusy(true); setMessage('');
    try {
      await createPair({ ...pair, student_two_id: pair.student_two_id || null }); setMessage('Pair created.'); setPair({ id:'', teacher_id:'', student_one_id:'', student_two_id:'', name:'', course_id:'' });
    } catch (e) { setMessage(e instanceof Error ? e.message : 'Could not create pair.'); } finally { setBusy(false); }
  }

  const teachers = list.filter((u) => u.role === 'teacher');
  const students = list.filter((u) => u.role === 'student');

  async function changeTeacherCourseAccess(teacherId: string, courseId: string, unlocked: boolean) {
    const key = teacherId + ':' + courseId;
    setAccessBusy(key);
    setMessage('');
    try {
      await setTeacherCourseAccess(teacherId, courseId, unlocked);
      setMessage(unlocked ? 'Course opened for the teacher. All lessons and blocks are now available.' : 'Course access closed for the teacher.');
      await refresh();
    } catch (e) {
      setMessage(e instanceof Error ? e.message : 'Could not change teacher access.');
    } finally {
      setAccessBusy('');
    }
  }

  function teacherHasCourse(teacherId: string, courseId: string) {
    return teacherAccess?.access.some((item) =>
      item.teacher_id === teacherId && item.course_id === courseId && item.unlocked === 1,
    ) ?? false;
  }

  return <main className="teacher-page">
    <header className="teacher-header"><div><p className="eyebrow">TALKUP · ADMIN</p><h1>School control</h1><p>Create teachers and students, then connect them into lesson pairs.</p></div><div className="admin-header-actions"><button className="back" onClick={onOpenMaterials}>Materials</button><button className="back" onClick={async () => { await logout(); onLogout(); }}>Sign out</button></div></header>
    {message && <div className="panel" role="status">{message}</div>}
    {(userLoadError || courseLoadError) && <section className="panel" role="alert"><h2>Some school data could not be loaded</h2><p>One failed request will no longer hide the data from the other request. Use Retry after checking the message below.</p>{userLoadError && <p><strong>Users:</strong> {userLoadError}</p>}{courseLoadError && <p><strong>Courses:</strong> {courseLoadError}</p>}<button type="button" className="back" disabled={loading} onClick={() => void refresh()}>{loading ? 'Retrying…' : 'Retry loading data'}</button></section>}
    <div className="admin-grid">
      <section className="panel"><h2>Create user</h2><form className="admin-form" onSubmit={addUser}>
        <input placeholder="User ID" value={user.id} onChange={e=>setUser({...user,id:e.target.value})} required />
        <select value={user.role} onChange={e=>setUser({...user,role:e.target.value as 'teacher'|'student'})}><option value="teacher">Teacher</option><option value="student">Student</option></select>
        <input placeholder="Name" value={user.name} onChange={e=>setUser({...user,name:e.target.value})} required />
        <input type="email" placeholder="Email" value={user.email} onChange={e=>setUser({...user,email:e.target.value})} required />
        <input type="password" minLength={10} placeholder="Temporary password" value={user.password} onChange={e=>setUser({...user,password:e.target.value})} required />
        <button disabled={busy}>Create user</button>
      </form></section>
      <section className="panel"><h2>Create pair</h2><form className="admin-form" onSubmit={addPair}>
        <input placeholder="Pair ID" value={pair.id} onChange={e=>setPair({...pair,id:e.target.value})} required />
        <input placeholder="Pair name" value={pair.name} onChange={e=>setPair({...pair,name:e.target.value})} required />
        <select value={pair.teacher_id} onChange={e=>setPair({...pair,teacher_id:e.target.value})} required><option value="">Select teacher</option>{teachers.map(u=><option key={u.id} value={u.id}>{u.name} · {u.email}</option>)}</select>
        <select value={pair.student_one_id} onChange={e=>setPair({...pair,student_one_id:e.target.value})} required><option value="">Select student 1</option>{students.map(u=><option key={u.id} value={u.id}>{u.name} · {u.email}</option>)}</select>
        <select value={pair.student_two_id} onChange={e=>setPair({...pair,student_two_id:e.target.value})}><option value="">No second student</option>{students.map(u=><option key={u.id} value={u.id}>{u.name} · {u.email}</option>)}</select>
        <select value={pair.course_id} onChange={e=>setPair({...pair,course_id:e.target.value})} required><option value="">Assign course</option>{courseList.map(c=><option key={c.id} value={c.id}>{c.language.toUpperCase()} · {c.level} · {c.title}</option>)}</select>
        <button disabled={busy}>Create pair</button>
      </form></section>
    </div>
    <section className="panel admin-access-panel">
      <div className="section-heading">
        <div>
          <p className="eyebrow">ACCESS CONTROL</p>
          <h2>Teacher access</h2>
          <p>Choose exactly which courses each teacher can open. Opening a course gives that teacher access to every lesson and every content block inside it.</p>
        </div>
        <span className="status-pill">{teacherAccess?.teachers.length ?? teachers.length} teachers</span>
      </div>
      <div className="teacher-access-list">
        {(teacherAccess?.teachers ?? teachers.map(u => ({ id:u.id, name:u.name, email:u.email, created_at:u.created_at }))).map(teacher => (
          <article className="teacher-access-card" key={teacher.id}>
            <div className="teacher-access-header">
              <div><h3>{teacher.name}</h3><p>{teacher.email || teacher.id}</p></div>
              <span className="role-badge">TEACHER</span>
            </div>
            <div className="teacher-course-grid">
              {(teacherAccess?.courses ?? courseList).map(course => {
                const key = teacher.id + ':' + course.id;
                const open = teacherHasCourse(teacher.id, course.id);
                return <button
                  key={course.id}
                  type="button"
                  className={open ? 'teacher-course-toggle is-open' : 'teacher-course-toggle'}
                  disabled={accessBusy === key}
                  onClick={() => void changeTeacherCourseAccess(teacher.id, course.id, !open)}
                  aria-pressed={open}
                >
                  <span><strong>{course.language.toUpperCase()} · {course.level.toUpperCase()}</strong><small>{course.title}</small></span>
                  <span>{accessBusy === key ? 'Saving…' : open ? 'OPEN' : 'LOCKED'}</span>
                </button>;
              })}
            </div>
          </article>
        ))}
        {(!teacherAccess?.teachers.length && !teachers.length) && <p>No teachers have been created yet.</p>}
      </div>
    </section>
    <section className="panel"><h2>Users</h2><div className="user-list">{list.map(u=><div className="user-row" key={u.id}><b>{u.name}</b><span>{u.role}</span><small>{u.email}</small></div>)}</div></section>
  </main>;
}
